import { Button } from "@/components/ui/baseUi/button"
import { Checkbox } from "@/components/ui/baseUi/checkbox"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/baseUi/dialog"
import { Label } from "@/components/ui/baseUi/label"
import { Text } from "@/components/ui/baseUi/text"
import type { BoxSlotTemplateGroupFormikEntity } from "@/modules/boxSlotTemplate/entities/boxSlotTemplateGroupFormikEntity"
import { cn } from "@/modules/lib/utils"
import { type SimpleFormik } from "@/typings/formik"
import { InformationAlertIcon } from "@images/svgIcons/informationAlertIcon"
import { type BoxSlotTemplateGroupInputKey } from "@modules/boxSlotTemplate/boxSlotTemplateGroupFormikTypes"
import { type Weekdays } from "@modules/boxSlotTemplate/boxSlotTemplateTypes"
import { DailySlotTemplates } from "@modules/boxSlotTemplate/components/dailySlotTemplates"
import { word } from "@modules/core/utils/i18n"
import { HUCardAlert } from "@modules/ui/components/huCardAlert"
import { useLocaleWeekDays } from "@modules/utils/hooks/useWeekdays"
import { ArrowLeftIcon, LoaderCircle } from "lucide-react"
import { useState } from "react"

export function BoxSlotTemplateGroupScheduleForm({
  formik,
  moveToSettingsStep,
  submitForm,
  isSubmitting,
  className,
  ...props
}: {
  formik: SimpleFormik<BoxSlotTemplateGroupFormikEntity, BoxSlotTemplateGroupInputKey> & {
    setFieldTouched: (field: string, touched?: boolean, shouldValidate?: boolean) => void
  }
  moveToSettingsStep: () => void
  submitForm: () => void
  isSubmitting: boolean
  className?: string
} & React.ComponentProps<"div">) {
  const localeWeekdays = useLocaleWeekDays()
  const [displayCopyPasteDialog, setDisplayCopyPasteDialog] = useState<boolean>(false)
  const [isSubmittingDialog, setIsSubmittingDialog] = useState<boolean>(true)
  const [selectedDay, setSelectedDay] = useState<Weekdays | null>(null)
  const daysOfWeek = localeWeekdays.map((weekdays) => ({
    name: weekdays.value,
    label: weekdays.label,
    checked: false,
  }))

  const [days, setDays] = useState(daysOfWeek)

  const handleCheckboxCopyPaste = (index: number) => {
    const newDays = [...days]
    newDays[index]!.checked = !newDays[index]!.checked

    const atLeastOneChecked = newDays.some((day) => day.checked)
    setIsSubmittingDialog(!atLeastOneChecked)
    setDays(newDays)
  }

  const copyRange = () => {
    if (selectedDay === null) return

    const selectedSlotTemplates = formik.values.slotTemplatesRanges.filter((range) => range.weekDay === selectedDay)

    const currentMaxIndex = formik.values.slotTemplatesRanges.reduce(
      (max, range) => (range.index > max ? range.index : max),
      -1,
    )

    let newIndex = currentMaxIndex + 1
    const newSlotTemplates = selectedSlotTemplates.flatMap((template) =>
      days
        .filter((day) => day.checked)
        .map((day) => ({
          ...template,
          weekDay: day.name,
          index: newIndex++,
        })),
    )

    const updatedSlotTemplatesRanges = [...formik.values.slotTemplatesRanges, ...newSlotTemplates]
    const sortedSlotTemplatesRanges = updatedSlotTemplatesRanges.sort(
      (a, b) => Number(a.startTime) - Number(b.startTime),
    )
    void formik.setFieldValue("slotTemplatesRanges", sortedSlotTemplatesRanges)
  }

  const saveDialogCopyPaste = (): void => {
    copyRange()
    onHideDialogCopyPaste()
  }

  const onHideDialogCopyPaste = (): void => {
    const resetDays = days.map((day) => ({ ...day, checked: false }))
    setDays(resetDays)
    setIsSubmittingDialog(true)
    setDisplayCopyPasteDialog(false)
  }

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <div className="flex flex-col gap-2">
        <Text fontStyle="TM">{word("box.slotTemplateGroup.form.schedule")}</Text>
        <HUCardAlert
          title={""}
          description={word("box.slotTemplateGroup.form.scheduleExplanation")}
          icon={
            <InformationAlertIcon
              className="text-primary-600"
              color="currentColor"
              backgroundColor="var(--primary-100)"
            />
          }
        />
      </div>

      <div className="flex flex-col gap-2">
        <Text fontStyle="LS" style={{ lineHeight: "21px", marginBottom: 7 }}>
          {word("box.slotTemplate.form.recurrence").toUpperCase()}
          <Text fontStyle="LL" className="text-red-500">
            {" *"}
          </Text>
        </Text>

        <div className="flex flex-col gap-6">
          {localeWeekdays.map((weekday, weekdayIndex) => (
            <DailySlotTemplates
              key={weekdayIndex}
              weekday={weekday.value}
              formik={formik}
              setDisplayCopyPasteDialog={setDisplayCopyPasteDialog}
              setSelectedDay={setSelectedDay}
            />
          ))}
        </div>
      </div>

      <div className="mt-3 flex justify-end pb-4">
        <div className="flex grow flex-row gap-3">
          <Button size="lg" variant="outline" className="flex-1" onClick={moveToSettingsStep}>
            <ArrowLeftIcon color="currentColor" />
            {word("global.previous")}
          </Button>
          <Button
            isLoading={isSubmitting}
            size="lg"
            variant="primary"
            className="mr-2 flex-1"
            disabled={isSubmitting}
            onClick={submitForm}
          >
            {word("box.slot.form.submit.button")}
            {isSubmitting && <LoaderCircle className="animate-spin" />}
          </Button>
        </div>
      </div>

      <Dialog open={displayCopyPasteDialog} onOpenChange={onHideDialogCopyPaste}>
        <DialogContent className="mt-3 flex w-[500px] flex-col rounded-4xl">
          {/* HEADER */}
          <DialogHeader className="flex flex-col flex-wrap content-center justify-center gap-2 pt-5 text-center">
            <DialogTitle>
              <Text fontStyle="TL" className="text-textDefault-heading">
                {word("box.slotTemplate.form.dialogTitle.copyPaste")}
              </Text>
            </DialogTitle>
          </DialogHeader>
          {/* CONTENT */}
          <div className="mt-2 mb-4 ml-2 flex flex-col gap-2 capitalize">
            {days.map((day, dayIndex) => (
              <div key={dayIndex} className="flex flex-row items-center gap-2">
                <Checkbox
                  id={day.name}
                  checked={day.name === selectedDay ? true : day.checked}
                  onCheckedChange={() => handleCheckboxCopyPaste(dayIndex)}
                  disabled={day.name === selectedDay ? true : false}
                />
                <Label htmlFor={day.name} className="text-lg text-textDefault-body">
                  {day.label}
                </Label>
              </div>
            ))}
          </div>

          <Button size="lg" variant="primary" disabled={isSubmittingDialog} onClick={() => saveDialogCopyPaste()}>
            {word("global.check")}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  )
}
